import { useCallback, useEffect, useRef, useState } from "react";
import { CheckCircle, Loader2, Clock, ArrowRight, RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "src/store/store";
import { fetchPatientProfile } from "src/store/reducers/authReducer";

/** How long to keep re-reading the profile before telling the patient it is slow. */
const MAX_POLLS = 8;
const POLL_MS = 3000;
const REDIRECT_SECONDS = 6;

export default function PaymentSuccessPage() {
    const navigate = useNavigate();
    const dispatch = useDispatch<any>();

    const pk = useSelector((state: RootState) => state.auth.pk);
    const hasPaidBefore = useSelector((state: RootState) => state.auth.hasPaidBefore);
    const paymentStatus = useSelector((state: RootState) => state.auth.paymentStatus);
    const profile = useSelector((state: RootState) => state.auth.profile);

    // The same two fields the payment gate reads, so this page can never say
    // "confirmed" while the gate still considers the patient unpaid.
    const isConfirmed = hasPaidBefore || paymentStatus === "paid";
    const reference: string | undefined = profile?.last_payment_id;

    const [polls, setPolls] = useState(0);
    const [secondsLeft, setSecondsLeft] = useState(REDIRECT_SECONDS);
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const goToProfile = useCallback(
        () => navigate(pk ? `/patient-details/PersonalInformation?patientId=${pk}` : "/patient-details"),
        [navigate, pk],
    );

    /* ── Re-read GET /patients/profile until the payment shows up ──────────
       Stripe returns the patient here before its webhook has necessarily
       reached our backend, so `payment_status` / `last_payment_id` can still be
       missing for a few seconds. */
    const startPolling = useCallback(() => {
        if (pollRef.current) clearInterval(pollRef.current);
        setPolls(0);
        dispatch(fetchPatientProfile());
        pollRef.current = setInterval(() => {
            setPolls((n) => {
                const next = n + 1;
                if (next >= MAX_POLLS && pollRef.current) clearInterval(pollRef.current);
                return next;
            });
            dispatch(fetchPatientProfile());
        }, POLL_MS);
    }, [dispatch]);

    useEffect(() => {
        startPolling();
        return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }, [startPolling]);

    // Stop polling the moment the payment lands.
    useEffect(() => {
        if (isConfirmed && pollRef.current) clearInterval(pollRef.current);
    }, [isConfirmed]);

    /* ── Only a CONFIRMED payment redirects ───────────────────────────────
       Sending an unconfirmed patient onward just bounces them off the payment
       gate and back into onboarding. */
    useEffect(() => {
        if (!isConfirmed) return;
        const tick = setInterval(() => setSecondsLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
        const done = setTimeout(goToProfile, REDIRECT_SECONDS * 1000);
        return () => { clearInterval(tick); clearTimeout(done); };
    }, [isConfirmed, goToProfile]);

    const stillChecking = !isConfirmed && polls < MAX_POLLS;

    return (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center overflow-y-auto px-4 py-6 bg-gradient-to-br from-brand-50 via-white to-brand-50 dark:from-surface-dark dark:via-surface-dark dark:to-surface-dark-50">
            <div className="w-full max-w-[520px] bg-white dark:bg-surface-dark-50 rounded-2xl border border-surface-200/60 dark:border-surface-dark-200 shadow-[0_8px_32px_rgba(3,93,184,0.1)] dark:shadow-dark-lg p-6 sm:p-10 text-center">

                <div className="flex justify-center mb-5 sm:mb-6">
                    <div className={[
                        "w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center",
                        isConfirmed ? "bg-emerald-50 dark:bg-emerald-500/10"
                            : stillChecking ? "bg-brand-50 dark:bg-brand-500/10"
                                : "bg-amber-50 dark:bg-amber-500/10",
                    ].join(" ")}>
                        {isConfirmed
                            ? <CheckCircle size={42} className="text-emerald-500 dark:text-emerald-400" />
                            : stillChecking
                                ? <Loader2 size={40} className="text-brand-500 dark:text-brand-400 animate-spin" />
                                : <Clock size={40} className="text-amber-500 dark:text-amber-400" />}
                    </div>
                </div>

                {isConfirmed ? (
                    <>
                        <h2 className="text-heading-3 font-bold text-emerald-600 dark:text-emerald-400 mb-2">Payment Successful</h2>
                        <p className="text-body text-slate-500 dark:text-slate-400 mb-4">
                            Your payment is confirmed and your service is active. Thank you for choosing{" "}
                            <strong className="text-slate-700 dark:text-slate-200">Symbian Health</strong>.
                        </p>
                        <p className="text-body font-medium text-slate-700 dark:text-slate-300 mb-6">
                            Next, complete your profile and upload your medical documents so a specialist can review your case.
                        </p>
                    </>
                ) : stillChecking ? (
                    <>
                        <h2 className="text-heading-3 font-bold text-brand-500 dark:text-brand-400 mb-2">Confirming your payment…</h2>
                        <p className="text-body text-slate-500 dark:text-slate-400 mb-6">
                            Your payment went through. We are waiting for the confirmation to reach your account — this usually takes a few seconds. Please do not close this page.
                        </p>
                    </>
                ) : (
                    <>
                        <h2 className="text-heading-3 font-bold text-amber-600 dark:text-amber-400 mb-2">Payment received — still confirming</h2>
                        <p className="text-body text-slate-500 dark:text-slate-400 mb-6">
                            Your payment went through, but the confirmation has not reached your account yet. You can check again, or contact support with the reference below — no need to pay a second time.
                        </p>
                    </>
                )}

                {reference && (
                    <p className="text-ui-sm text-slate-400 dark:text-slate-300 mb-5 break-all">
                        Payment reference: <span className="font-mono text-slate-600 dark:text-slate-200">{reference}</span>
                    </p>
                )}

                <div className="flex flex-col items-center gap-3">
                    {isConfirmed ? (
                        <>
                            <button
                                onClick={goToProfile}
                                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 sm:py-2.5 bg-gradient-to-br from-brand-500 to-brand-700 text-white font-semibold rounded-xl shadow-[0_2px_8px_rgba(3,93,184,0.3)] hover:shadow-[0_4px_16px_rgba(3,93,184,0.4)] hover:-translate-y-px transition-all"
                            >
                                Complete My Profile
                                <ArrowRight size={16} />
                            </button>
                            <p className="text-ui-sm text-slate-400 dark:text-slate-300">Taking you there in {secondsLeft}s…</p>
                        </>
                    ) : stillChecking ? (
                        <p className="text-ui-sm text-slate-400 dark:text-slate-300">Checking… ({polls + 1}/{MAX_POLLS})</p>
                    ) : (
                        <button
                            onClick={startPolling}
                            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 sm:py-2.5 bg-white dark:bg-surface-dark-200 text-slate-700 dark:text-slate-200 font-semibold rounded-xl ring-1 ring-inset ring-slate-200 dark:ring-white/[0.12] hover:bg-slate-50 dark:hover:bg-surface-dark-300 transition-all"
                        >
                            <RefreshCw size={15} />
                            Check again
                        </button>
                    )}
                </div>

                <p className="text-ui-sm text-slate-400 dark:text-slate-300 mt-7">Symbian Health © {new Date().getFullYear()} All rights reserved.</p>
            </div>
        </div>
    );
}
